import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const Wallboards = () => (
  <Page
    title="Wallboards"
    intro="A wallboard turns a TV or spare screen into a full-screen, rotating
    display of live AmbuNet data — shifts, fleet readiness, KPIs, upcoming
    events and shoutouts — built for crew rooms, control rooms and base
    corridors. Displays refresh themselves and run unattended."
  >
    <Section title="Who can do this?">
      <Bullets>
        <Bullet>
          <strong>Viewing the wallboard</strong> while signed in (sidebar:{' '}
          <strong>System &gt; Wallboard</strong>) — Manager and Supervisor
          teams, and Site Administrators.
        </Bullet>
        <Bullet>
          <strong>Building screens and managing displays</strong> (sidebar:{' '}
          <strong>System &gt; Wallboard Settings</strong>) — Site
          Administrators only.
        </Bullet>
        <Bullet>
          <strong>Authorising a TV</strong> as a standalone display — a Site
          Administrator enters their credentials at the unlock screen.
        </Bullet>
        <Bullet>
          <strong>Sending a shoutout</strong> — any signed-in user, from the
          avatar menu.
        </Bullet>
      </Bullets>
      <Info>
        Wallboards are available on <strong>Premium</strong> and{' '}
        <strong>Enterprise</strong> plans.
      </Info>
    </Section>

    <Section title="Setting up a TV display">
      <p>
        A display doesn't need anyone to stay signed in. Instead, a Site
        Administrator authorises the device once, and the TV holds its own
        device token from then on.
      </p>
      <Step number={1} title="Open the unlock screen on the TV">
        Browse to AmbuNet on the TV and choose{' '}
        <strong>Set up a wallboard display</strong> from the login screen.
      </Step>
      <Step number={2} title="Authorise the device">
        A Site Administrator enters their username and password (plus their
        authenticator code if they have MFA set up) and gives the display a
        name — for example "Crew Room TV".
      </Step>
      <Step number={3} title="Walk away">
        The wallboard starts immediately. The administrator is{' '}
        <strong>not</strong> signed in on the TV — the display can only ever
        show the wallboard.
      </Step>
      <Warning>
        Site Administrators who sign in exclusively with a passkey can't
        authorise a display — the unlock screen needs a password (and
        authenticator-app MFA where configured). Use a password-holding Site
        Admin account instead.
      </Warning>
      <p>
        Signed-in managers can also just open{' '}
        <strong>System &gt; Wallboard</strong> to run the same display in the
        browser — handy for checking a board without a TV.
      </p>
    </Section>

    <Section title="Building your screens">
      <p>
        <strong>Wallboard Settings</strong> is a what-you-see-is-what-you-get
        editor: the canvas is a scaled miniature of the real TV, rendering your
        actual widgets with live data.
      </p>
      <Bullets>
        <Bullet>
          Add as many <strong>screens</strong> as you like; the display rotates
          through them, and each screen has its own dwell time.
        </Bullet>
        <Bullet>
          <strong>Drag</strong> a widget anywhere on the grid, and{' '}
          <strong>resize</strong> it from its corner handle — positions snap to
          the grid.
        </Bullet>
        <Bullet>
          If a resize collides with neighbours, they reflow automatically; the
          editor warns you if a screen is too full to show everything.
        </Bullet>
        <Bullet>
          Choose a <strong>dark or light theme</strong> per board; each display
          also has its own on-screen theme toggle.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="The widgets">
      <Bullets>
        <Bullet>
          <strong>Operations</strong> — Shifts today, Uncovered shifts (amber
          when above zero), On duty now, Shifts per week trend, Upcoming
          events with crewing status, PTS today.
        </Bullet>
        <Bullet>
          <strong>Clinical &amp; governance</strong> — Patient records count
          and trend (counts only), KPI section tiles with health score, CQC
          readiness score.
        </Bullet>
        <Bullet>
          <strong>Fleet</strong> — Fleet readiness: operational vs off-road
          vehicles and open defects.
        </Bullet>
        <Bullet>
          <strong>People &amp; messages</strong> — Shoutouts, a fixed Message
          of the day, and a Clock.
        </Bullet>
      </Bullets>
      <Info title="Privacy by design">
        Wallboards are meant for public walls, so widgets only ever show
        operational counts, staff names and the messages you write — never
        patient-level or financial data. The finance KPI section is excluded
        from the KPI widget.
      </Info>
    </Section>

    <Section title="Shoutouts">
      <p>
        Anyone can send a short message of recognition to a colleague: open
        the avatar menu (top right) and choose{' '}
        <strong>Send a Shoutout</strong>. The Shoutouts widget shows the latest
        ones on the wall. Messages are capped at 280 characters, and you can't
        shout out to yourself.
      </p>
    </Section>

    <Section title="Managing and revoking displays">
      <p>
        Every authorised display is listed in{' '}
        <strong>Wallboard Settings &gt; Devices</strong>, where a Site
        Administrator can rename it, point it at a different board, or revoke
        it. A revoked display locks itself again within a minute — no trip to
        the TV required.
      </p>
      <Info>
        Displays poll for fresh data every minute, so new shifts, defects and
        shoutouts appear on the wall without anyone touching the TV.
      </Info>
    </Section>

    <Section title="Related pages">
      <p>
        For the signed-in management view of the same organisation-wide
        numbers, see{' '}
        <DocLink
          docsPage
          url="/getting-started/global-overview"
          text="Global Overview"
        />
        .
      </p>
    </Section>
  </Page>
)

export default Wallboards
